---
title: Bring Your Entire Context to Your Agents with Skills
description: Entire Skills make the context behind your code changes easier for your coding agent to search, explain, hand off, and reuse.
category: Changelog
author: Alisha Kawaguchi and Rizèl Scarlett
image: /blog/bring-your-entire-context-to-your-agents-with-skills/skills-og.png
---

As agents become the primary interface for development, many developers are choosing to delegate command execution to their coding agents. The [Entire CLI](https://github.com/entireio/cli) already captures the context behind your code changes: prompts, transcripts, and the decisions that led to each change, alongside your git history. Today we're making that data easier for your agent to use.

## Make your agent more powerful

Our [skills](https://docs.entire.io/skills/overview) repository contains agent-invokable workflows that teach your agent how to use the Entire CLI. Instead of looking up syntax or running commands yourself, you can ask in plain language and let your agent drive the workflow.

This initial release gives your agent four context workflows you can see in action below:

### Search past work

The [`search`](https://github.com/entireio/skills/blob/main/skills/search/SKILL.md) skill finds prior work in your Entire history by topic, repo, branch, author, or time window, so your agent can bring past context into the current task before making changes.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "60.9375%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/4ff2b738a2634b5fb1a7e8c92d15bb60/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F4ff2b738a2634b5fb1a7e8c92d15bb60%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Search skill demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Explain why code exists

The [`explain`](https://github.com/entireio/skills/blob/main/skills/explain/SKILL.md) skill traces a function, file, or line back to the session that created it, so your agent can answer the intent behind code, not just describe what the code currently does.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "60.9375%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/8aaa7825db50d0fce603dcd12dc803a3/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F8aaa7825db50d0fce603dcd12dc803a3%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Explain skill demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Investigate changes

The [`what-happened`](https://github.com/entireio/skills/blob/main/skills/what-happened/SKILL.md) skill combines `git blame` with Entire checkpoint context to explain why a specific block of code looks the way it does. It is useful during code review or when you are investigating a regression.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "60.9375%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/479e60aa4ec94816e451b843ca6b0af7/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F479e60aa4ec94816e451b843ca6b0af7%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="What Happened skill demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Hand off between agents

The [`session-handoff`](https://github.com/entireio/skills/blob/main/skills/session-handoff/SKILL.md) skill picks up where another agent left off by reading the saved or active session and summarizing the task state, important discoveries, blockers, and next steps.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "66.875%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/54d377f7575db5f16e23ca6af6b22379/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F54d377f7575db5f16e23ca6af6b22379%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Session handoff skill demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

## Turn sessions into reusable workflows

The [`session-to-skill`](https://github.com/entireio/skills/blob/main/skills/session-to-skill/SKILL.md) skill turns repeated agent sessions into reusable workflows via `SKILL.md` drafts. It extracts the process from real work instead of asking you to write idealized instructions from scratch.

## Cross-agent by design

All skills are cross-agent. Install them once and use the same workflows in [Agent Skills](https://agentskills.io)-compatible tools, including Claude Code, Codex, Cursor, Copilot, Gemini CLI, and OpenCode.

## Try it

We published a [tutorial](https://docs.entire.io/skills/tutorial) for trying the skills from scratch. It walks through installation, and using the skills in a real codebase.
